import React from 'react';
import { Link, NavLink } from 'react-router-dom';
import logo from './images/logo.webp';
import { useDarkMode } from './shared';

const NAV_ITEMS = [
  { to: '/',         label: 'Messages' },
  { to: '/timeline', label: 'Timeline' },
  { to: '/sponsor',  label: 'Sponsor' },
  { to: '/about',    label: 'About' },
  { to: '/contact',  label: 'Contact' },
];

function NavItem({ to, label }) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) =>
        `px-2 md:px-3 py-1.5 rounded-none text-xs md:text-sm font-medium transition-colors ${
          isActive
            ? 'text-gray-900 dark:text-gray-50 bg-gray-100 dark:bg-gray-800'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-50 hover:bg-gray-50 dark:hover:bg-gray-800'
        }`
      }
    >
      {label}
    </NavLink>
  );
}

function DarkModeToggle({ dark, onToggle }) {
  return (
    <button
      onClick={onToggle}
      aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={dark ? 'Light mode' : 'Dark mode'}
      className="w-9 h-9 flex items-center justify-center rounded-none text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
    >
      {dark ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="5" />
          <line x1="12" y1="1" x2="12" y2="3" strokeLinecap="round" />
          <line x1="12" y1="21" x2="12" y2="23" strokeLinecap="round" />
          <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" strokeLinecap="round" />
          <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" strokeLinecap="round" />
          <line x1="1" y1="12" x2="3" y2="12" strokeLinecap="round" />
          <line x1="21" y1="12" x2="23" y2="12" strokeLinecap="round" />
          <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" strokeLinecap="round" />
          <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" strokeLinecap="round" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      )}
    </button>
  );
}

export default function Layout({ children }) {
  const [dark, setDark] = useDarkMode();
  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-gray-950 text-gray-900 dark:text-gray-50">

      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 sticky top-0 z-20">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          {/* Logo + Title */}
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
            <img src={logo} alt="MS Message Center logo" className="h-9 w-9 flex-shrink-0" />
            <span className="text-xl font-extrabold tracking-tight leading-none">
              <span className="text-blue-600">MS</span><span className="text-gray-900 dark:text-gray-50"> Message Center</span>
            </span>
          </Link>

          {/* Nav */}
          <nav aria-label="Main navigation" className="hidden md:flex items-center gap-1 ml-6">
            {NAV_ITEMS.map(item => <NavItem key={item.to} {...item} />)}
          </nav>

          {/* Controls + Social links */}
          <div className="ml-auto flex items-center gap-2">
            {/* LinkedIn */}
            <a href="https://www.linkedin.com/in/danielbradley2/" target="_blank" rel="noopener noreferrer"
              aria-label="LinkedIn" title="LinkedIn"
              className="w-9 h-9 flex items-center justify-center rounded-none text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
                <circle cx="4" cy="4" r="2" />
              </svg>
            </a>
            {/* X */}
            <a href="https://x.com/DanielatOCN" target="_blank" rel="noopener noreferrer"
              aria-label="X" title="X"
              className="w-9 h-9 flex items-center justify-center rounded-none text-gray-400 hover:text-gray-900 dark:hover:text-gray-50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.736-8.835L2.25 2.25h6.844l4.258 5.63 4.892-5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            {/* Blog */}
            <a href="https://ourcloudnetwork.com/" target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-none text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors whitespace-nowrap">
              Blog
            </a>
            {/* MSDocsTracker — text link */}
            <a href="https://msdocstracker.com/" target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center px-3 py-1.5 rounded-none text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors whitespace-nowrap">
              MSDocsTracker
            </a>
            {/* RSS Feed */}
            <a href="/feed.xml" target="_blank" rel="noopener noreferrer"
              aria-label="RSS Feed" title="RSS Feed"
              className="w-9 h-9 flex items-center justify-center rounded-none text-gray-400 hover:text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M6.18 15.64a2.18 2.18 0 110 4.36 2.18 2.18 0 010-4.36M4 4.44A15.56 15.56 0 0119.56 20h-2.83A12.73 12.73 0 006.18 7.27V4.44M4 10.1a9.9 9.9 0 019.9 9.9h-2.83A7.07 7.07 0 006.18 12.93V10.1H4z"/>
              </svg>
            </a>
            {/* Dark mode toggle */}
            <DarkModeToggle dark={dark} onToggle={() => setDark(d => !d)} />
          </div>
        </div>

        {/* Mobile nav */}
        <nav aria-label="Main navigation" className="md:hidden border-t border-gray-100 dark:border-gray-800 px-4 py-2 flex items-center justify-center gap-1">
          {NAV_ITEMS.map(item => <NavItem key={item.to} {...item} />)}
        </nav>
      </header>

      {/* Main */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 mt-10">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            &copy; {new Date().getFullYear()} MS Message Center — a community project by{' '}
            <a href="https://www.linkedin.com/in/danielbradley2/" target="_blank" rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 hover:underline">
              Daniel Bradley
            </a>
            . Not affiliated with Microsoft.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/timeline" className="hover:text-gray-900 dark:hover:text-gray-50">Timeline</Link>
            <Link to="/sponsor" className="hover:text-gray-900 dark:hover:text-gray-50">Sponsor</Link>
            <Link to="/about" className="hover:text-gray-900 dark:hover:text-gray-50">About</Link>
            <Link to="/contact" className="hover:text-gray-900 dark:hover:text-gray-50">Contact</Link>
            <a href="https://ourcloudnetwork.com/" target="_blank" rel="noopener noreferrer"
              className="hover:text-gray-900 dark:hover:text-gray-50">Blog</a>
            <a href="/feed.xml" target="_blank" rel="noopener noreferrer"
              className="hover:text-orange-500 flex items-center gap-1.5 transition-colors" title="RSS Feed">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M6.18 15.64a2.18 2.18 0 110 4.36 2.18 2.18 0 010-4.36M4 4.44A15.56 15.56 0 0119.56 20h-2.83A12.73 12.73 0 006.18 7.27V4.44M4 10.1a9.9 9.9 0 019.9 9.9h-2.83A7.07 7.07 0 006.18 12.93V10.1H4z"/>
              </svg>
              RSS
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
